import { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_AD_ASSETS, GET_AD_ASSET_BY_ID } from '@/graphql/ads/ad-assets-queries'
import getCroppedImg from '@/utils/crop-image'
import { constructProxyUrl } from '@/utils/proxy'

// Utility function to detect media type
const getMediaType = (fileName) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm']
    const lowerName = fileName.toLowerCase()
    return videoExtensions.some((ext) => lowerName.includes(ext)) ? 'video' : 'image'
}

export const useCreativeAssets = (creativeId, format = 'singleImage', campaignId = null) => {
    const [assetData, setAssetData] = useState(null)
    const [croppedImageUrl, setCroppedImageUrl] = useState(null)
    const [carouselAssets, setCarouselAssets] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Fetch assets for the creative (using adId parameter)
    const [fetchCreativeAssets] = useLazyQuery(GET_AD_ASSETS, {
        onCompleted: (data) => {
            const assets = data?.AdAsset?.adAssets
            if (assets && assets.length > 0) {
                if (format === 'carousel') {
                    // Enhanced carousel handling like useCarouselCards
                    const enhancedCarouselAssets = assets.slice(0, 5).map((asset) => {
                        const proxyUrl = constructProxyUrl(asset.url)
                        return {
                            id: asset.id,
                            url: proxyUrl,
                            croppedUrl: proxyUrl,
                            type: getMediaType(asset.name),
                            width: asset.width,
                            height: asset.height,
                            name: asset.name,
                            cropData: asset.cropData || [],
                            // Add carousel-specific fields
                            headline: asset.headline || '',
                            description: asset.description || '',
                        }
                    })
                    setCarouselAssets(enhancedCarouselAssets)

                    // Fetch detailed crop data for all carousel assets
                    enhancedCarouselAssets.forEach((asset) => {
                        fetchAssetDetail({ variables: { id: asset.id } })
                    })
                } else {
                    // For single image/video format, get first asset
                    const firstAsset = assets[0]
                    fetchAssetDetail({ variables: { id: firstAsset.id } })
                }
            } else {
                console.log('No assets found for creative:', creativeId)
                setError('No assets found for this creative')
            }
            setLoading(false)
        },
        onError: (err) => {
            console.error('Creative Assets failed:', err)
            setError(err)
            setLoading(false)
        },
    })

    // Fetch individual asset with crop data
    const [fetchAssetDetail] = useLazyQuery(GET_AD_ASSET_BY_ID, {
        onCompleted: async (data) => {
            if (data?.AdAsset?.adAsset) {
                const asset = data.AdAsset.adAsset
                const proxyUrl = constructProxyUrl(asset.url)

                const assetInfo = {
                    url: proxyUrl,
                    type: getMediaType(asset.name),
                    width: asset.width,
                    height: asset.height,
                    cropData: asset.cropData || [],
                    thumbnails: asset.thumbnails
                }

                // Set asset data for single image/video formats
                if (format !== 'carousel') {
                    setAssetData(assetInfo)
                }

                // Handle format-specific cropping
                if (asset.cropData && asset.cropData.length > 0) {
                    // Determine which crop to use based on format
                    let targetCrop = null

                    if (format === 'carousel') {
                        // For carousel, prefer square crop
                        targetCrop = asset.cropData.find((crop) => crop.name === 'square')
                    } else {
                        // For other formats, try to match the format name
                        targetCrop =
                            asset.cropData.find((crop) => crop.name === format) ||
                            asset.cropData.find((crop) => crop.name === 'square')
                    }

                    // Fallback to first crop if no specific crop found
                    if (!targetCrop && asset.cropData.length > 0) {
                        targetCrop = asset.cropData[0]
                    }

                    if (targetCrop) {
                        try {
                            const croppedImage = await getCroppedImg(proxyUrl, targetCrop, 0)

                            if (format === 'carousel') {
                                // Update specific carousel asset
                                setCarouselAssets((prev) =>
                                    prev.map((carouselAsset) =>
                                        carouselAsset.id === asset.id
                                            ? { ...carouselAsset, croppedUrl: croppedImage, cropData: asset.cropData }
                                            : carouselAsset,
                                    ),
                                )
                            } else {
                                // Update main asset
                                setCroppedImageUrl(croppedImage)
                                setAssetData((prev) => ({ ...prev, cropData: asset.cropData }))
                            }
                        } catch (error) {
                            if (format === 'carousel') {
                                setCarouselAssets((prev) =>
                                    prev.map((carouselAsset) =>
                                        carouselAsset.id === asset.id
                                            ? { ...carouselAsset, croppedUrl: proxyUrl, cropData: asset.cropData }
                                            : carouselAsset,
                                    ),
                                )
                            } else {
                                setCroppedImageUrl(proxyUrl)
                            }
                        }
                    } else {
                        // No crop data available, use original
                        if (format === 'carousel') {
                            setCarouselAssets((prev) =>
                                prev.map((carouselAsset) =>
                                    carouselAsset.id === asset.id
                                        ? { ...carouselAsset, croppedUrl: proxyUrl }
                                        : carouselAsset,
                                ),
                            )
                        } else {
                            setCroppedImageUrl(proxyUrl)
                        }
                    }
                } else {
                    // No crop data available, use original URL
                    if (format === 'carousel') {
                        setCarouselAssets((prev) =>
                            prev.map((carouselAsset) =>
                                carouselAsset.id === asset.id
                                    ? { ...carouselAsset, croppedUrl: proxyUrl }
                                    : carouselAsset,
                            ),
                        )
                    } else {
                        setCroppedImageUrl(proxyUrl)
                    }
                }
            }
        },
        onError: (err) => {
            console.error('Asset Detail failed:', err)
            setError(err)
        },
    })

    // Load assets when creative ID is available
    useEffect(() => {
        if (creativeId) {
            setLoading(true)
            // Reset state
            setAssetData(null)
            setCroppedImageUrl(null)
            setCarouselAssets([])
            setError(null)

            fetchCreativeAssets({
                variables: {
                    campaignId: campaignId,
                    adId: creativeId,
                },
            })
        } else {
            // Reset state when no creativeId
            setAssetData(null)
            setCroppedImageUrl(null)
            setCarouselAssets([])
            setError(null)
            setLoading(false)
        }
    }, [creativeId, format])

    return {
        assetData,
        croppedImageUrl,
        carouselAssets,
        loading,
        error,
    }
}
